import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ResponsiveLine } from '@nivo/line';
import numeral from 'numeral';

import SidebarLeft from '../components/SidebarLeft/SidebarLeft';
import Header from '../components/Header';
import Metrics from '../components/Tools/Metrics';
import DCF from '../components/Tools/DCF';
import IncomeGraph from '../components/Tools/IncomeGraph';
import BalanceGraph from '../components/Tools/BalanceGraph';
import axios from '../../api/axios';

const BOARD_VIEWS = ['overview', 'trading', 'value'];

const defaultMarket = {
    ticker: 'NVDA',
    company_name: 'NVDA',
    description: '',
    industry: '',
    asset_class: 'Equity',
    price: 0,
    open: 0,
    high: 0,
    low: 0,
    previous_close: 0,
    volume: 0,
    shares: 0,
    market_cap: 0,
    percent_change: 0,
    fifty_two_week_range: { low: 0, high: 0 },
    chart: [],
    source: 'unavailable',
    warnings: [],
};

const seedNews = [
    {
        outlet: 'Market Desk',
        headline: 'Analysts are watching revenue durability and margin trends.',
    },
    {
        outlet: 'Macro Wire',
        headline: 'Rates and liquidity remain key inputs for growth multiples.',
    },
    {
        outlet: 'Finspace',
        headline: 'Community notes point to catalyst timing and position sizing.',
    },
];

const formatCurrency = (value) => numeral(value || 0).format('$0,0.00');
const formatCompactCurrency = (value) => numeral(value || 0).format('$0.00a');
const formatNumber = (value) => numeral(value || 0).format('0.00a');
const formatPercent = (value) => numeral(value || 0).format('0.00%');

const latestYear = (statements) => {
    const years = Object.keys(statements || {}).sort();
    return years[years.length - 1];
};

const valueFromStatement = (statement, key) => {
    const entry = (statement || []).find((item) => key in item);
    return entry ? entry[key] : 0;
};

const buildYearData = (statements) =>
    Object.keys(statements || {})
        .sort()
        .map((year) => {
            const incomeStatement = statements[year].income_statement || [];
            const balanceSheet = statements[year].balance_sheet || [];
            const revenue = valueFromStatement(incomeStatement, 'revenues');
            const expenses = valueFromStatement(
                incomeStatement,
                'operating_expenses'
            );
            const profitLoss = valueFromStatement(
                incomeStatement,
                'net_income_loss'
            );
            const assets = valueFromStatement(balanceSheet, 'assets');
            const equity = valueFromStatement(balanceSheet, 'equity');

            return {
                year,
                revenue,
                expenses,
                profitLoss,
                assets,
                equity,
                liabilities: assets - equity,
            };
        });

const buildFallbackSeries = (ticker, price) => {
    const base = price || 100;
    return [
        0.94, 0.98, 0.96, 1.02, 1.05, 1.03, 1.08, 1.04, 1.11, 1.09, 1.13,
        1.1,
    ].map((multiplier, index) => ({
        date: `2024-${String(index + 1).padStart(2, '0')}-01`,
        price: Number((base * multiplier).toFixed(2)),
        ticker,
    }));
};

const lineSeriesFromMarket = (market) => {
    const points =
        market.chart && market.chart.length
            ? market.chart
            : buildFallbackSeries(market.ticker, market.price);
    return [
        {
            id: market.ticker || 'Ticker',
            data: points.map((point) => ({
                x: point.date,
                y: point.price,
            })),
        },
    ];
};

export const calculateBreakevenRate = (risk, reward) => {
    const numericRisk = Number(risk) || 0;
    const numericReward = Number(reward) || 0;
    if (numericRisk <= 0 || numericReward <= 0) {
        return 0;
    }
    return numericRisk / (numericRisk + numericReward);
};

const SentimentMeter = ({ label, value = 'hold' }) => {
    const options = ['sell', 'hold', 'buy'];
    return (
        <div className="sentiment-meter">
            {label && <span className="sentiment-label">{label}</span>}
            <div className="sentiment-track">
                {options.map((option) => (
                    <span
                        key={option}
                        className={`sentiment-dot ${
                            value === option ? 'sentiment-dot-active' : ''
                        }`}
                    />
                ))}
            </div>
            <div className="sentiment-options">
                <span>Sell</span>
                <span>Hold</span>
                <span>Buy</span>
            </div>
        </div>
    );
};

const BoardTabs = ({ activeView, onChange }) => (
    <div className="board-tabs" role="tablist" aria-label="Board views">
        {BOARD_VIEWS.map((view) => (
            <button
                key={view}
                type="button"
                className={`board-tab ${activeView === view ? 'active' : ''}`}
                onClick={() => onChange(view)}
            >
                {view === 'trading'
                    ? 'Trading'
                    : view.charAt(0).toUpperCase() + view.slice(1)}
            </button>
        ))}
    </div>
);

const TickerLineChart = ({ market, compact = false }) => (
    <div className={compact ? 'board-line-chart compact' : 'board-line-chart'}>
        <ResponsiveLine
            data={lineSeriesFromMarket(market)}
            margin={{ top: 20, right: 32, bottom: 52, left: 58 }}
            xScale={{ type: 'point' }}
            yScale={{ type: 'linear', min: 'auto', max: 'auto', stacked: false }}
            axisTop={null}
            axisRight={null}
            axisBottom={{
                tickSize: 0,
                tickPadding: 12,
                tickRotation: compact ? -45 : 0,
            }}
            axisLeft={{
                tickSize: 0,
                tickPadding: 10,
                format: (value) => numeral(value).format('$0a'),
            }}
            colors={['#3bc6cf']}
            pointSize={4}
            pointBorderWidth={2}
            pointBorderColor={{ from: 'serieColor' }}
            useMesh={true}
            enableGridX={false}
            enableSlices="x"
            curve="monotoneX"
        />
    </div>
);

const OverviewView = ({ market, metricsData, searchQuery, yearDataList }) => {
    const latest = yearDataList[yearDataList.length - 1] || {};
    const action =
        metricsData.PE && metricsData.PE < 35
            ? 'buy'
            : market.percent_change < -0.02
            ? 'sell'
            : 'hold';

    return (
        <div className="board-view-grid overview-grid">
            <section className="board-card market-summary-card">
                <div className="board-card-header">
                    <div>
                        <p className="board-eyebrow">{searchQuery}</p>
                        <h4>{market.company_name || searchQuery}</h4>
                    </div>
                    <SentimentMeter value={action} />
                </div>
                <div className="market-summary">
                    <div>
                        <span>Price</span>
                        <strong>{formatCurrency(market.price)}</strong>
                    </div>
                    <div>
                        <span>Percent Change</span>
                        <strong
                            className={
                                market.percent_change >= 0
                                    ? 'positive'
                                    : 'negative'
                            }
                        >
                            {formatPercent(market.percent_change)}
                        </strong>
                    </div>
                    <div>
                        <span>Asset Class</span>
                        <strong>{market.asset_class || 'Equity'}</strong>
                    </div>
                    <div>
                        <span>Market Cap</span>
                        <strong>{formatCompactCurrency(market.market_cap)}</strong>
                    </div>
                </div>
                <div className="market-detail-table">
                    <span>Open</span>
                    <strong>{formatCurrency(market.open)}</strong>
                    <span>Previous Close</span>
                    <strong>{formatCurrency(market.previous_close)}</strong>
                    <span>Day Range</span>
                    <strong>
                        {formatCurrency(market.low)} -{' '}
                        {formatCurrency(market.high)}
                    </strong>
                    <span>52 Week Range</span>
                    <strong>
                        {formatCurrency(market.fifty_two_week_range?.low)} -{' '}
                        {formatCurrency(market.fifty_two_week_range?.high)}
                    </strong>
                    <span>Volume</span>
                    <strong>{formatNumber(market.volume)}</strong>
                    <span>Shares</span>
                    <strong>{formatNumber(market.shares)}</strong>
                    <span>Latest Revenue</span>
                    <strong>{formatCompactCurrency(latest.revenue)}</strong>
                    <span>Latest Net Income</span>
                    <strong>{formatCompactCurrency(latest.profitLoss)}</strong>
                </div>
            </section>
            <section className="board-card chart-card">
                <div className="board-card-header">
                    <div>
                        <p className="board-eyebrow">Market chart</p>
                        <h4>{searchQuery} trend</h4>
                    </div>
                    <div className="range-tabs">
                        <span className="active">1D</span>
                        <span>1M</span>
                        <span>6M</span>
                        <span>1Y</span>
                    </div>
                </div>
                <TickerLineChart market={market} />
            </section>
            <section className="board-card company-card">
                <p className="board-eyebrow">{market.industry || 'Company'}</p>
                <h4>{market.company_name || searchQuery}</h4>
                <p>
                    {market.description ||
                        'Company profile is unavailable from the market data provider. Use the value tab to inspect available fundamentals.'}
                </p>
                <div className="signal-stack">
                    <SentimentMeter label="Macro" value="hold" />
                    <SentimentMeter label="Trading" value={action} />
                    <SentimentMeter
                        label="Value"
                        value={metricsData.PE < 35 ? 'buy' : 'hold'}
                    />
                </div>
            </section>
            <section className="board-card notes-card">
                <h4>Catalyst</h4>
                <textarea
                    aria-label="Catalyst notes"
                    placeholder="What will cause this trade or investment to work?"
                />
                <h4>Notes</h4>
                <textarea
                    aria-label="Strategy notes"
                    placeholder="Take notes about strategy, macro impacts, asset managers, and risks."
                />
            </section>
            <section className="board-card news-card">
                <h4>News</h4>
                <div className="news-grid">
                    {seedNews.map((item) => (
                        <article key={item.headline}>
                            <strong>{item.outlet}</strong>
                            <span>{item.headline}</span>
                        </article>
                    ))}
                </div>
            </section>
        </div>
    );
};

const TradingView = ({ market, searchQuery }) => {
    const storageKey = `finspace:trade-log:${searchQuery}`;
    const [tradeLog, setTradeLog] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem(storageKey)) || [];
        } catch (error) {
            return [];
        }
    });
    const [tradeForm, setTradeForm] = useState({
        mode: 'long',
        buyPrice: market.price || '',
        target: market.price ? (market.price * 1.08).toFixed(2) : '',
        stop: market.price ? (market.price * 0.96).toFixed(2) : '',
        portfolioTotal: '10000',
    });

    useEffect(() => {
        localStorage.setItem(storageKey, JSON.stringify(tradeLog));
    }, [storageKey, tradeLog]);

    useEffect(() => {
        if (market.price) {
            setTradeForm((current) => ({
                ...current,
                buyPrice: current.buyPrice || market.price.toFixed(2),
                target: current.target || (market.price * 1.08).toFixed(2),
                stop: current.stop || (market.price * 0.96).toFixed(2),
            }));
        }
    }, [market.price]);

    const buyPrice = Number(tradeForm.buyPrice) || 0;
    const target = Number(tradeForm.target) || 0;
    const stop = Number(tradeForm.stop) || 0;
    const risk = Math.abs(buyPrice - stop);
    const reward = Math.abs(target - buyPrice);
    const breakevenRate = calculateBreakevenRate(risk, reward);

    const updateTradeForm = (field, value) => {
        setTradeForm((current) => ({ ...current, [field]: value }));
    };

    const handleLogTrade = () => {
        const nextTrade = {
            id: `#${String(tradeLog.length + 1).padStart(3, '0')}`,
            asset: searchQuery,
            mode: tradeForm.mode,
            buyPrice,
            risk,
            reward,
            breakevenRate,
            result: 'Open',
        };
        setTradeLog((current) => [nextTrade, ...current].slice(0, 8));
    };

    return (
        <div className="board-view-grid trading-grid">
            <section className="board-card trading-chart-card">
                <div className="board-card-header">
                    <div>
                        <p className="board-eyebrow">Technical chart</p>
                        <h4>{searchQuery} price action</h4>
                    </div>
                    <SentimentMeter value="buy" />
                </div>
                <TickerLineChart market={market} />
            </section>
            <section className="board-card trade-calculator-card">
                <div className="board-card-header">
                    <div>
                        <p className="board-eyebrow">Trade Calculator</p>
                        <h4>Position setup</h4>
                    </div>
                    <div className="board-mode-toggle">
                        {['long', 'short'].map((mode) => (
                            <button
                                key={mode}
                                type="button"
                                className={
                                    tradeForm.mode === mode ? 'active' : ''
                                }
                                onClick={() => updateTradeForm('mode', mode)}
                            >
                                {mode}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="trade-form-grid">
                    <label>
                        <span>Portfolio Total</span>
                        <input
                            type="number"
                            value={tradeForm.portfolioTotal}
                            onChange={(event) =>
                                updateTradeForm(
                                    'portfolioTotal',
                                    event.target.value
                                )
                            }
                        />
                    </label>
                    <label>
                        <span>Buy Price</span>
                        <input
                            type="number"
                            value={tradeForm.buyPrice}
                            onChange={(event) =>
                                updateTradeForm('buyPrice', event.target.value)
                            }
                        />
                    </label>
                    <label>
                        <span>Target</span>
                        <input
                            type="number"
                            value={tradeForm.target}
                            onChange={(event) =>
                                updateTradeForm('target', event.target.value)
                            }
                        />
                    </label>
                    <label>
                        <span>Stop Loss</span>
                        <input
                            type="number"
                            value={tradeForm.stop}
                            onChange={(event) =>
                                updateTradeForm('stop', event.target.value)
                            }
                        />
                    </label>
                </div>
                <div className="trade-result-grid">
                    <span>Risk</span>
                    <strong>{formatCurrency(risk)}</strong>
                    <span>Reward</span>
                    <strong>{formatCurrency(reward)}</strong>
                    <span>Breakeven Rate</span>
                    <strong>{formatPercent(breakevenRate)}</strong>
                </div>
                <button
                    type="button"
                    className="btn finspace-action"
                    onClick={handleLogTrade}
                >
                    <i className="bi bi-arrow-right-short" /> Log
                </button>
            </section>
            <section className="board-card trading-log-card">
                <div className="board-card-header">
                    <div>
                        <p className="board-eyebrow">Trading Log</p>
                        <h4>Win Rate: 40%</h4>
                    </div>
                </div>
                <div className="board-table-wrap">
                    <table className="board-table">
                        <thead>
                            <tr>
                                <th>Trade ID</th>
                                <th>Asset</th>
                                <th>Breakeven</th>
                                <th>Buy Price</th>
                                <th>Win/Loss</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(tradeLog.length
                                ? tradeLog
                                : [
                                      {
                                          id: '#001',
                                          asset: searchQuery,
                                          breakevenRate: 0.25,
                                          buyPrice: market.price,
                                          result: 'Open',
                                      },
                                  ]
                            ).map((trade) => (
                                <tr key={`${trade.id}-${trade.asset}`}>
                                    <td>{trade.id}</td>
                                    <td>{trade.asset}</td>
                                    <td>{formatPercent(trade.breakevenRate)}</td>
                                    <td>{formatCurrency(trade.buyPrice)}</td>
                                    <td>{trade.result}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
};

const ValueView = ({ metricsData, yearDataList }) => (
    <div className="section-board-columns">
        <div className="section-board-columns-left">
            <Metrics data={metricsData} />
            <DCF />
        </div>
        <div className="section-board-columns-right">
            <div className="section-board-statement">
                <h4 className="widget-header">Balance Sheet</h4>
                <BalanceGraph data={yearDataList} />
            </div>
            <div className="section-board-statement">
                <h4 className="widget-header">Income Statement</h4>
                <IncomeGraph data={yearDataList} />
            </div>
        </div>
    </div>
);

const Boards = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const requestedView = searchParams.get('view') || 'overview';
    const activeView = BOARD_VIEWS.includes(requestedView)
        ? requestedView
        : 'overview';
    const [statements, setStatements] = useState({});
    const [metrics, setMetrics] = useState({});
    const [ratios, setRatios] = useState({});
    const [risks, setRisks] = useState({});
    const [price, setPrice] = useState(0);
    const [searchPayload, setSearchPayload] = useState('NVDA');
    const [searchQuery, setSearchQuery] = useState('NVDA');
    const [isLoading, setIsLoading] = useState(false);
    const [errMsg, setErrMsg] = useState('');
    const [sourceMeta, setSourceMeta] = useState({});
    const [warnings, setWarnings] = useState([]);
    const [market, setMarket] = useState(defaultMarket);

    useEffect(() => {
        const fetchBoardData = async () => {
            setIsLoading(true);
            setErrMsg('');
            try {
                const [dashboardResponse, marketResponse] = await Promise.all([
                    axios.get(`/api/boards/${searchQuery}/`),
                    axios.get(`/api/boards/${searchQuery}/market/`),
                ]);
                setStatements(dashboardResponse.data.statements);
                setMetrics(dashboardResponse.data.metrics);
                setRatios(dashboardResponse.data.ratios);
                setRisks(dashboardResponse.data.risk);
                setPrice(dashboardResponse.data.price);
                setSourceMeta({
                    financialsSource: dashboardResponse.data.financials_source,
                    marketDataSource:
                        dashboardResponse.data.market_data_source ||
                        marketResponse.data.source,
                    asOf: dashboardResponse.data.as_of,
                });
                setWarnings([
                    ...(dashboardResponse.data.warnings || []),
                    ...(marketResponse.data.warnings || []),
                ]);
                setMarket({ ...defaultMarket, ...marketResponse.data });
            } catch (err) {
                setStatements({});
                setMetrics({});
                setRatios({});
                setRisks({});
                setPrice(0);
                setSourceMeta({});
                setWarnings([]);
                setMarket({
                    ...defaultMarket,
                    ticker: searchQuery,
                    company_name: searchQuery,
                });
                if (err.response) {
                    setErrMsg(
                        err.response.data?.detail ||
                            `Could not load ${searchQuery}.`
                    );
                } else {
                    setErrMsg('Unable to reach the boards API.');
                }
            } finally {
                setIsLoading(false);
            }
        };
        fetchBoardData();
    }, [searchQuery]);

    const yearDataList = useMemo(() => buildYearData(statements), [statements]);
    const latestStatementYear = latestYear(statements);

    const metricsData = {
        SPSGrowth: metrics.sales_growth,
        EPSGrowth: metrics.earnings_growth,
        BVPSGrowth: metrics.equity_growth,
        PROFMargin: metrics.profit_margin,
        AVGROA: metrics.average_roa,
        AVGROE: metrics.average_roe,
        Price: price || market.price,
        PR: ratios.revenues,
        PE: ratios.net_income_loss,
        PBV: ratios.equity,
        DPE: risks.net_income_loss,
        DPBV: risks.equity,
    };

    const handleSearchChange = (e) => {
        setSearchPayload(e.target.value.toUpperCase());
    };

    const handleSearchSubmit = (event) => {
        event.preventDefault();
        const nextTicker = searchPayload.trim().toUpperCase();
        if (!nextTicker) {
            setErrMsg('Enter a ticker symbol.');
            return;
        }
        setSearchPayload(nextTicker);
        setSearchQuery(nextTicker);
    };

    const setActiveView = (view) => {
        setSearchParams(view === 'overview' ? {} : { view });
    };

    return (
        <div className="page-board">
            <div className="subpage-board">
                <div className="board-section-sidebar">
                    <SidebarLeft />
                </div>
                <div className="board-section-center">
                    <div className="board-header-profile">
                        <Header />
                    </div>
                    <div className="board-toolbar">
                        <div>
                            <h3 className="board-title">Analysis Boards</h3>
                            <p className="board-subtitle">
                                {searchQuery}
                                {latestStatementYear
                                    ? ` · FY ${latestStatementYear}`
                                    : ''}
                            </p>
                        </div>
                        <form
                            className="board-search"
                            onSubmit={handleSearchSubmit}
                        >
                            <input
                                type="text"
                                className="form-control form-control-md"
                                placeholder="Ticker"
                                value={searchPayload}
                                onChange={handleSearchChange}
                            />
                            <button
                                className="btn icon-button board-search-button"
                                type="submit"
                                aria-label="Search ticker"
                            >
                                <i className="bi bi-search bi-icon-md" />
                            </button>
                        </form>
                    </div>
                    <div className="board-navigation">
                        <SentimentMeter value="hold" />
                        <BoardTabs
                            activeView={activeView}
                            onChange={setActiveView}
                        />
                    </div>
                    {(isLoading || errMsg) && (
                        <div
                            className={`board-status ${
                                errMsg ? 'board-status-error' : ''
                            }`}
                        >
                            {errMsg || `Loading ${searchQuery}...`}
                        </div>
                    )}
                    {!isLoading && !errMsg && (
                        <div className="board-source">
                            <span>
                                Financials:{' '}
                                {sourceMeta.financialsSource || 'unknown'}
                            </span>
                            <span>
                                Market:{' '}
                                {sourceMeta.marketDataSource || market.source}
                            </span>
                            {sourceMeta.asOf && (
                                <span>
                                    As of{' '}
                                    {new Date(sourceMeta.asOf).toLocaleString()}
                                </span>
                            )}
                        </div>
                    )}
                    {!isLoading &&
                        !errMsg &&
                        warnings.map((warning) => (
                            <div
                                className="board-status board-status-warning"
                                key={warning}
                            >
                                {warning}
                            </div>
                        ))}
                    {!isLoading && !errMsg && activeView === 'overview' && (
                        <OverviewView
                            market={market}
                            metricsData={metricsData}
                            searchQuery={searchQuery}
                            yearDataList={yearDataList}
                        />
                    )}
                    {!isLoading && !errMsg && activeView === 'trading' && (
                        <TradingView
                            market={market}
                            searchQuery={searchQuery}
                        />
                    )}
                    {!isLoading && !errMsg && activeView === 'value' && (
                        <ValueView
                            metricsData={metricsData}
                            yearDataList={yearDataList}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default Boards;
